<template>
  <div class="d-flex align-items-end flex-wrap" style="gap: 10px">
    <!-- View type + period picker stay together on one line -->
    <div class="cd-period-group">
    <!-- View type: day / month / year -->
    <div class="cd-seg">
      <button
        type="button"
        class="cd-seg-btn"
        :class="{ on: viewType === 'day' }"
        :disabled="loading"
        @click="setViewType('day')"
      >รายวัน</button>
      <button
        type="button"
        class="cd-seg-btn"
        :class="{ on: viewType === 'month' }"
        :disabled="loading"
        @click="setViewType('month')"
      >รายเดือน</button>
      <button
        type="button"
        class="cd-seg-btn"
        :class="{ on: viewType === 'year' }"
        :disabled="loading"
        @click="setViewType('year')"
      >รายปี</button>
    </div>

    <!-- Period picker — fixed-width slot so the row never shifts on view switch -->
    <div class="cd-period-slot">
      <b-form-select
        v-if="viewType === 'month'"
        v-model="selectedMonth"
        :options="monthOptions"
        size="sm"
        class="cd-period-select"
        :disabled="loading"
      />
      <b-form-select
        v-else-if="viewType === 'year'"
        v-model="selectedYear"
        :options="yearOptions"
        size="sm"
        class="cd-period-select"
        :disabled="loading"
      />
      <b-form-datepicker
        v-else
        v-model="selectedDay"
        size="sm"
        class="cd-period-select cd-period-datepicker"
        :max="todayStr"
        locale="th"
        :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
        :disabled="loading"
      />
    </div>
    </div>

    <!-- Comparison: toggle for month/day; รายปี เทียบปีก่อนอย่างเดียว (ไม่มี toggle) -->
    <div v-if="viewType !== 'year'" class="cd-seg cd-seg--ghost">
      <button
        type="button"
        class="cd-seg-btn"
        :class="{ on: comparison === 'prevPeriod' }"
        :disabled="loading"
        @click="comparison = 'prevPeriod'"
      >เทียบ {{ prevPeriodLabel }}</button>
      <button
        type="button"
        class="cd-seg-btn"
        :class="{ on: comparison === 'prevYear' }"
        :disabled="loading"
        @click="comparison = 'prevYear'"
      >เทียบ {{ prevYearLabel }}</button>
    </div>
    <span v-else class="cd-cmp-static">เทียบ {{ prevYearLabel }}</span>
  </div>
</template>

<script>
import moment from "moment";
import {
  buildMonthOptions,
  buildYearOptions,
  rangeFor,
  comparisonLabels,
} from "@/utils/dashboardPeriod";

export default {
  name: "PeriodSelector",

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    initial: {
      type: Object,
      default: null,
    },
  },

  data() {
    const init = this.initial || {};
    const initView = init.viewType || "month";
    return {
      viewType: initView,
      selectedMonth: init.month || moment().format("YYYY-MM"),
      selectedYear: init.year || moment().format("YYYY"),
      selectedDay: init.day || moment().format("YYYY-MM-DD"),
      // รายปี: เทียบปีก่อนอย่างเดียว (ไม่มี toggle)
      comparison: initView === "year" ? "prevYear" : init.comparison || "prevPeriod",
      monthOptions: buildMonthOptions(),
      yearOptions: buildYearOptions(),
    };
  },

  computed: {
    todayStr() {
      return moment().format("YYYY-MM-DD");
    },

    comparisonLabelPair() {
      return comparisonLabels(this.viewType, {
        month: this.selectedMonth,
        year: this.selectedYear,
        day: this.selectedDay,
      });
    },

    prevPeriodLabel() {
      return this.comparisonLabelPair.prevPeriodLabel;
    },

    prevYearLabel() {
      return this.comparisonLabelPair.prevYearLabel;
    },

    currentRange() {
      return rangeFor(this.viewType, {
        month: this.selectedMonth,
        year: this.selectedYear,
        day: this.selectedDay,
      });
    },

    changeKey() {
      return JSON.stringify({
        v: this.viewType,
        m: this.selectedMonth,
        y: this.selectedYear,
        d: this.selectedDay,
        c: this.comparison,
      });
    },
  },

  mounted() {
    this.emitChange();
  },

  watch: {
    changeKey() {
      this.emitChange();
    },
  },

  methods: {
    setViewType(type) {
      if (this.viewType === type) return;
      const wasYear = this.viewType === "year";
      this.viewType = type;
      // Reset selection to current period for the new view type
      if (type === "month") {
        this.selectedMonth = moment().format("YYYY-MM");
      } else if (type === "year") {
        this.selectedYear = moment().format("YYYY");
      } else {
        this.selectedDay = moment().format("YYYY-MM-DD");
      }
      // รายปี: ซ่อน toggle → บังคับเทียบปีก่อนเสมอ; ออกจากรายปี → กลับค่าเริ่มต้น
      if (type === "year") this.comparison = "prevYear";
      else if (wasYear) this.comparison = "prevPeriod";
    },

    emitChange() {
      const { startDt, endDt } = this.currentRange;
      this.$emit("change", {
        startDt,
        endDt,
        viewType: this.viewType,
        comparison: this.comparison,
        prevPeriodLabel: this.prevPeriodLabel,
        prevYearLabel: this.prevYearLabel,
      });
    },
  },
};
</script>

<style scoped>
/* ---- Segmented control (mirrors ClinicDashboard.vue .cd-seg) ---- */
.cd-seg {
  display: inline-flex;
  background: #fff;
  border: 1px solid #d2dae6;
  border-radius: 9px;
  padding: 3px;
}
.cd-seg--ghost {
  border-color: #e5eaf0;
}
.cd-seg-btn {
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  background: none;
  border: 0;
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.15s;
  white-space: nowrap;
}
.cd-seg-btn:disabled { cursor: not-allowed; opacity: 0.6; }
.cd-seg-btn.on {
  background: #2196f3;
  color: #fff;
}
.cd-seg--ghost .cd-seg-btn.on {
  background: #e3f2fd;
  color: #1976d2;
}

/* View type toggle + period picker stay on one line (never wrap apart). */
.cd-period-group {
  display: inline-flex;
  align-items: flex-end;
  flex-wrap: nowrap;
  gap: 10px;
}

/* Fixed-width slot: all three pickers occupy the same footprint so the row
   to the right (comparison toggle) never shifts when switching view type. */
.cd-period-slot {
  flex: 0 0 180px;
  width: 180px;
}
.cd-period-slot >>> .custom-select,
.cd-period-slot >>> .b-form-datepicker {
  width: 100%;
  min-width: 0;
}

/* Period dropdown — match the segmented pickers (36px, 9px radius). */
.cd-period-select.custom-select {
  height: 36px;
  border-radius: 9px;
  border-color: #d2dae6;
  font-size: 12px;
  font-weight: 600;
}

/* b-form-datepicker — match the selects WITHOUT leaking into the calendar dropdown.
   The grid is a <div class="b-calendar-grid form-control"> and the nav/day cells are
   generic .btn, so we scope strictly to the root, the trigger (.dropdown-toggle), and
   the selected-value <label class="form-control"> only. */
.cd-period-datepicker {
  height: 36px;
  border-radius: 9px;
  border-color: #d2dae6;
  font-size: 12px;
  font-weight: 600;
}
.cd-period-datepicker >>> .btn.dropdown-toggle {
  padding: 0 6px;
}
.cd-period-datepicker >>> label.form-control {
  height: 34px;
  margin: 0;
  padding: 4px 6px;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.8;
  border: 0;
}

/* Single static comparison label for รายปี (no toggle). */
.cd-cmp-static {
  font-size: 12px;
  font-weight: 600;
  color: #1976d2;
  background: #e3f2fd;
  border: 1px solid #e5eaf0;
  border-radius: 9px;
  padding: 8px 12px;
  white-space: nowrap;
}
</style>
